import { useId, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { cn } from '../cn';

export interface DisclosureProps {
  /** The always-visible row: title, pills, a summary line. */
  readonly summary: React.ReactNode;
  /** What expanding reveals. */
  readonly children: React.ReactNode;
  readonly defaultOpen?: boolean;
  /** Something on the right of the summary that must stay clickable on its own, e.g. a tracking link. */
  readonly aside?: React.ReactNode;
  readonly className?: string;
  /**
   * `card` draws its own border; `row` draws none, for a disclosure that is
   * one row of a ruled list (the list draws the rules).
   */
  readonly variant?: 'card' | 'row';
}

/**
 * A row that expands to show more. The whole summary is the control, so a
 * click anywhere on it opens the detail; the chevron says which way it is.
 * Reads as a button to a keyboard and a screen reader, and the panel is
 * labelled by it.
 *
 * @param props - The summary, the detail, and whether to start open.
 * @returns The disclosure.
 */
export function Disclosure({
  summary,
  children,
  defaultOpen = false,
  aside,
  className,
  variant = 'card',
}: DisclosureProps): React.ReactElement {
  const id = useId();
  const [open, setOpen] = useState(defaultOpen);
  const row = variant === 'row';

  return (
    <div
      className={cn(
        'bg-white',
        row ? '' : 'rounded-portal-card border border-portal-line',
        className,
      )}
    >
      <div className={cn('flex items-center gap-2', row ? 'pr-5' : 'pr-3')}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`${id}-panel`}
          id={`${id}-summary`}
          onClick={() => setOpen((current) => !current)}
          className={cn(
            'flex min-w-0 flex-1 items-center gap-3 py-3 text-left hover:bg-portal-sky/50 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none',
            row ? 'px-5' : 'px-4',
          )}
        >
          <ChevronDownIcon
            aria-hidden
            className={cn(
              'size-4 shrink-0 text-portal-muted transition-transform',
              open ? 'rotate-180' : '',
            )}
          />
          <span className="min-w-0 flex-1">{summary}</span>
        </button>
        {aside === undefined ? null : <span className="shrink-0">{aside}</span>}
      </div>
      <div
        id={`${id}-panel`}
        role="region"
        aria-labelledby={`${id}-summary`}
        hidden={!open}
        className={cn('border-t border-portal-line', row ? 'px-5 pt-3 pb-4' : 'px-4 py-3')}
      >
        {children}
      </div>
    </div>
  );
}
